/* eslint-disable no-mixed-operators */
import type * as THREE from 'three'
import { Raycaster, Vector2 } from 'three'
import throttle from 'lodash/throttle'
import type { Mesh } from '../../export'
import { InteractiveEvent, InteractiveObject } from './node'

export type InteractionManagerOptions = {
  bindEventsOnBodyElement?: boolean
}

/**
 * InteractionManager 类
 * 用于管理 3D 场景中的用户交互（鼠标和触摸事件）。
 * 它负责监听 DOM 事件，通过射线检测（Raycasting）将 2D 屏幕坐标映射到 3D 空间，
 * 并向相应的 3D 对象分发自定义交互事件。
 */
export class InteractionManager {
  renderer: THREE.WebGLRenderer
  camera: THREE.Camera
  domElement: HTMLElement
  bindEventsOnBodyElement: boolean
  mouse: Vector2
  supportsPointerEvents: boolean
  interactiveObjects: InteractiveObject[] = []
  closestObject: InteractiveObject | null
  raycaster: THREE.Raycaster
  treatTouchEventsAsMouseEvents = true

  // Optimization: Track which event types are actually being used
  private activeEventTypes: Map<string, number> = new Map()
  private needsContinuousUpdate = false
  private needsIntersectionUpdate = false
  private lastMousePosition = new Vector2(-999, -999)
  private intersectionDirty = false
  private _downPosition = new Vector2()

  // Add callback for discrete event updates
  private discreteEventCallback: (() => void) | null = null

  public setDiscreteEventCallback = (callback: () => void) => {
    this.discreteEventCallback = callback
  }

  /**
   * 构造函数
   * @param renderer Three.js 渲染器
   * @param camera Three.js 相机
   * @param domElement 监听事件的 DOM 元素
   * @param options 配置选项
   */
  constructor(
    renderer: THREE.WebGLRenderer,
    camera: THREE.Camera,
    domElement: HTMLElement,
    options?: InteractionManagerOptions,
  ) {
    this.renderer = renderer
    this.camera = camera
    this.domElement = domElement
    this.bindEventsOnBodyElement = options && typeof options.bindEventsOnBodyElement !== 'undefined'
      ? options.bindEventsOnBodyElement
      : true

    this.mouse = new Vector2(-1, 1)
    this.supportsPointerEvents = !!window.PointerEvent
    this.interactiveObjects = []
    this.closestObject = null

    this.raycaster = new Raycaster()

    this.initHandleEvent()
  }

  /**
   * 初始化事件监听
   * 绑定鼠标、触摸和指针事件到 DOM 元素或文档。
   */
  private initHandleEvent = () => {
    const domElement = this.domElement
    const events: (keyof HTMLElementEventMap)[] = ['click', 'dblclick', 'contextmenu']

    events.forEach((eventType: any) => {
      domElement.addEventListener(eventType, this.onMouseEvent)
    })

    if (this.supportsPointerEvents) {
      if (this.bindEventsOnBodyElement) {
        domElement.ownerDocument.addEventListener(
          'pointermove',
          this.onDocumentPointerMove,
        )
      }
      else {
        domElement.addEventListener('pointermove', this.onDocumentPointerMove)
      }
      domElement.addEventListener('pointerdown', this.onPointerDown)
      domElement.addEventListener('pointerup', this.onPointerUp)
    }

    if (this.bindEventsOnBodyElement) {
      domElement.ownerDocument.addEventListener(
        'mousemove',
        this.onDocumentMouseMove,
      )
    }
    else {
      domElement.addEventListener('mousemove', this.onDocumentMouseMove)
    }
    domElement.addEventListener('mousedown', this.onMouseDown)
    domElement.addEventListener('mouseup', this.onMouseUp)
    domElement.addEventListener('touchstart', this.onTouchStart, {
      passive: true,
    })
    domElement.addEventListener('touchmove', this.onTouchMove, {
      passive: true,
    })
    domElement.addEventListener('touchend', this.onTouchEnd, {
      passive: true,
    })

    this.treatTouchEventsAsMouseEvents = true
  }

  /**
   * 销毁实例
   * 移除所有事件监听器，清理资源。
   */
  public dispose = () => {
    const events: (keyof HTMLElementEventMap)[] = ['click', 'dblclick', 'contextmenu']

    events.forEach((eventType: any) => {
      this.domElement.removeEventListener(eventType, this.onMouseEvent)
    })

    if (this.supportsPointerEvents) {
      if (this.bindEventsOnBodyElement) {
        this.domElement.ownerDocument.removeEventListener(
          'pointermove',
          this.onDocumentPointerMove,
        )
      }
      else {
        this.domElement.removeEventListener(
          'pointermove',
          this.onDocumentPointerMove,
        )
      }
      this.domElement.removeEventListener('pointerdown', this.onPointerDown)
      this.domElement.removeEventListener('pointerup', this.onPointerUp)
    }

    if (this.bindEventsOnBodyElement) {
      this.domElement.ownerDocument.removeEventListener(
        'mousemove',
        this.onDocumentMouseMove,
      )
    }
    else {
      this.domElement.removeEventListener(
        'mousemove',
        this.onDocumentMouseMove,
      )
    }
    this.domElement.removeEventListener('mousedown', this.onMouseDown)
    this.domElement.removeEventListener('mouseup', this.onMouseUp)
    this.domElement.removeEventListener('touchstart', this.onTouchStart)
    this.domElement.removeEventListener('touchmove', this.onTouchMove)
    this.domElement.removeEventListener('touchend', this.onTouchEnd)
  }

  /**
   * 添加交互对象
   * 将 3D 对象注册到交互管理器中，使其能接收交互事件。
   * @param object 目标 3D 对象
   * @param eventTypes 可选，指定该对象需要监听的事件类型，用于性能优化
   */
  public add = (object: THREE.Object3D, eventTypes?: string[]) => {
    if (!object || this.interactiveObjects.find(i => i.target === object))
      return

    const interactiveObject = new InteractiveObject(object, object.name)
    this.interactiveObjects.push(interactiveObject)

    // Track event types for optimization
    if (eventTypes) {
      eventTypes.forEach((type) => {
        this.activeEventTypes.set(type, (this.activeEventTypes.get(type) || 0) + 1)
      })
      this.updateOptimizationFlags()
    }
  }

  private updateOptimizationFlags = () => {
    const continuousEvents = ['mousemove', 'pointermove', 'touchmove', 'mouseover', 'mouseout', 'mouseenter', 'mouseleave']
    const intersectionEvents = ['click', 'dblclick', 'contextmenu', 'mousedown', 'mouseup', 'pointerdown', 'pointerup', 'touchstart', 'touchend', ...continuousEvents]

    this.needsContinuousUpdate = continuousEvents.some(event => (this.activeEventTypes.get(event) || 0) > 0)
    this.needsIntersectionUpdate = intersectionEvents.some(event => (this.activeEventTypes.get(event) || 0) > 0)
  }

  /**
   * 移除交互对象
   * 从交互管理器中注销 3D 对象。
   * @param object 目标 3D 对象
   * @param childNames 可选，如果指定，仅移除特定名称的子对象
   */
  public remove = (object: THREE.Object3D, childNames: string[] = []) => {
    if (!object)
      return

    if (childNames.length > 0) {
      childNames.forEach((name) => {
        const child = object.getObjectByName(name)
        if (child) {
          this.interactiveObjects = this.interactiveObjects.filter(
            o => o.target !== child,
          )
        }
      })
    }
    else {
      this.interactiveObjects = this.interactiveObjects.filter(
        o => o.target !== object,
      )
    }
  }

  private onDocumentMouseMove = (mouseEvent: MouseEvent) => {
    this.mapPositionToPoint(this.mouse, mouseEvent.clientX, mouseEvent.clientY)

    // Optimization: Check if mouse position actually changed
    if (!this.needsContinuousUpdate
        || (this.lastMousePosition.distanceTo(this.mouse) < 0.001))
      return

    this.lastMousePosition.copy(this.mouse)
    this.intersectionDirty = true

    const event = new InteractiveEvent('mousemove', mouseEvent)

    this.interactiveObjects.forEach((object) => {
      this.dispatch(object, event)
    })
  }

  private onDocumentPointerMove = (pointerEvent: PointerEvent) => {
    this.mapPositionToPoint(
      this.mouse,
      pointerEvent.clientX,
      pointerEvent.clientY,
    )

    const event = new InteractiveEvent('pointermove', pointerEvent)

    this.interactiveObjects.forEach((object) => {
      this.dispatch(object, event)
    })
  }

  private onTouchMove = (touchEvent: TouchEvent) => {
    if (touchEvent.touches.length > 0) {
      this.mapPositionToPoint(
        this.mouse,
        touchEvent.touches[0].clientX,
        touchEvent.touches[0].clientY,
      )
    }

    const event = new InteractiveEvent(
      this.treatTouchEventsAsMouseEvents ? 'mousemove' : 'touchmove',
      touchEvent,
    )

    this.interactiveObjects.forEach((object) => {
      this.dispatch(object, event)
    })
  }

  private onMouseEvent = (mouseEvent: MouseEvent) => {
    if (mouseEvent.type === 'contextmenu')
      mouseEvent.preventDefault()

    // 修复：拖动场景旋转的时候，当鼠标释放会触发一个click事件
    if (mouseEvent.type === 'click') {
      const moveDistance = new Vector2(mouseEvent.clientX, mouseEvent.clientY).distanceTo(this._downPosition)
      if (moveDistance > 2)
        return
    }

    // For discrete events, trigger update if not already running continuously
    if (!this.needsContinuousUpdate) {
      this.intersectionDirty = true
      if (this.discreteEventCallback)
        this.discreteEventCallback()
      else
        this.update()
    }

    const event = new InteractiveEvent(mouseEvent.type, mouseEvent)

    const object = this.interactiveObjects.find((object) => {
      return object.intersected && (object.target.type === 'Group' || object.target.type === 'Mesh' || object.target.type === 'Poi')
    })

    // console.log('🚀 ~ InteractionManager ~ object:', object)
    if (object)
      this.dispatch(object, event)
  }

  private onMouseDown = (mouseEvent: MouseEvent) => {
    this._downPosition.set(mouseEvent.clientX, mouseEvent.clientY)
    this.mapPositionToPoint(this.mouse, mouseEvent.clientX, mouseEvent.clientY)

    // For discrete events, trigger update if not already running continuously
    if (!this.needsContinuousUpdate) {
      this.intersectionDirty = true
      if (this.discreteEventCallback)
        this.discreteEventCallback()
      else
        this.update()
    }

    const event = new InteractiveEvent('mousedown', mouseEvent)

    const object = this.interactiveObjects.find((object) => {
      return object.intersected && (object.target.type === 'Group' || object.target.type === 'Mesh' || object.target.type === 'Poi')
    })

    if (object)
      this.dispatch(object, event)
  }

  private onPointerDown = (pointerEvent: PointerEvent) => {
    this._downPosition.set(pointerEvent.clientX, pointerEvent.clientY)
    this.mapPositionToPoint(
      this.mouse,
      pointerEvent.clientX,
      pointerEvent.clientY,
    )

    this.update()

    const event = new InteractiveEvent('pointerdown', pointerEvent)

    const object = this.interactiveObjects.find((object) => {
      return object.intersected && (object.target.type === 'Group' || object.target.type === 'Mesh' || object.target.type === 'Poi')
    })

    if (object)
      this.dispatch(object, event)
  }

  private onTouchStart = (touchEvent: TouchEvent) => {
    if (touchEvent.touches.length > 0) {
      this._downPosition.set(touchEvent.touches[0].clientX, touchEvent.touches[0].clientY)
      this.mapPositionToPoint(
        this.mouse,
        touchEvent.touches[0].clientX,
        touchEvent.touches[0].clientY,
      )
    }

    this.update()

    const event = new InteractiveEvent(
      this.treatTouchEventsAsMouseEvents ? 'mousedown' : 'touchstart',
      touchEvent,
    )

    const object = this.interactiveObjects.find((object) => {
      return object.intersected && (object.target.type === 'Group' || object.target.type === 'Mesh' || object.target.type === 'Poi')
    })

    if (object)
      this.dispatch(object, event)
  }

  private onMouseUp = (mouseEvent: MouseEvent) => {
    const event = new InteractiveEvent('mouseup', mouseEvent)

    const object = this.interactiveObjects.find((object) => {
      return object.intersected && (object.target.type === 'Group' || object.target.type === 'Mesh' || object.target.type === 'Poi')
    })

    if (object)
      this.dispatch(object, event)
  }

  private onPointerUp = (pointerEvent: PointerEvent) => {
    const event = new InteractiveEvent('pointerup', pointerEvent)

    const object = this.interactiveObjects.find((object) => {
      return object.intersected && (object.target.type === 'Group' || object.target.type === 'Mesh' || object.target.type === 'Poi')
    })

    if (object)
      this.dispatch(object, event)
  }

  private onTouchEnd = (touchEvent: TouchEvent) => {
    if (touchEvent.touches.length > 0) {
      this.mapPositionToPoint(
        this.mouse,
        touchEvent.touches[0].clientX,
        touchEvent.touches[0].clientY,
      )
    }

    this.update()

    const event = new InteractiveEvent(
      this.treatTouchEventsAsMouseEvents ? 'mouseup' : 'touchend',
      touchEvent,
    )

    const object = this.interactiveObjects.find((object) => {
      return object.intersected && (object.target.type === 'Group' || object.target.type === 'Mesh' || object.target.type === 'Poi')
    })

    if (object)
      this.dispatch(object, event)
  }

  private mapPositionToPoint = (point: Vector2, x: number, y: number) => {
    const rect = this.renderer.domElement.getBoundingClientRect()

    point.x = ((x - rect.left) / rect.width) * 2 - 1
    point.y = -((y - rect.top) / rect.height) * 2 + 1
  }

  /**
   * 分发事件
   * 将交互事件分发给目标 3D 对象。
   * @param object 交互对象
   * @param event 交互事件
   */
  private dispatch = (object: InteractiveObject, event: InteractiveEvent) => {
    // console.log("🚀 ~ InteractionManager ~ object:", object)
    if (object.target && !event.cancelBubble) {
      event.coords = this.mouse
      event.distance = object.distance
      event.intersected = object.intersected
      event.point = object.point
      event.newClosestMesh = object.newClosestMesh
      object.target.dispatchEvent(event as any)
    }
  }

  /**
   * 检测射线相交
   * 使用射线投射器检测鼠标/触摸位置与 3D 对象的相交情况。
   * 计算最近的交点，并更新对象的相交状态。
   * @param object 待检测的交互对象
   */
  private checkIntersection = (object: InteractiveObject) => {
    if (!object.target.visible) {
      object.intersected = false
      return
    }

    const intersects = this.raycaster.intersectObjects([object.target], true)

    object.wasIntersected = object.intersected

    // get nearest intersection
    if (intersects.length > 0) {
      let distance = intersects[0].distance
      let newClosestMesh = intersects[0].object

      intersects.forEach((i) => {
        if (i.distance < distance) {
          newClosestMesh = i.object
          distance = i.distance
        }
      })

      object.newClosestMesh = newClosestMesh as Mesh
      object.intersected = true
      object.point = intersects[0].point
      object.distance = distance
    }
    else {
      object.intersected = false
    }
  }

  /**
   * 基础更新函数
   * 执行射线检测，处理 hover (enter/leave/over/out) 等状态变化。
   * 根据 activeEventTypes 判断是否需要执行检测。
   */
  private baseUpdateFn = () => {
    // Optimization: Only update intersections if needed
    if (!this.needsIntersectionUpdate || !this.intersectionDirty)
      return

    this.raycaster.setFromCamera(this.mouse, this.camera)

    this.interactiveObjects.forEach((object) => {
      if (object.target)
        this.checkIntersection(object)
    })

    this.interactiveObjects.sort((a, b) => {
      return a.distance - b.distance
    })

    // Only process hover events if continuous events are active
    if (this.needsContinuousUpdate) {
      const newClosestObject = this.interactiveObjects.find(object => object.intersected) ?? null

      if (newClosestObject !== this.closestObject) {
        if (this.closestObject) {
          const eventOutClosest = new InteractiveEvent('mouseout')
          this.dispatch(this.closestObject, eventOutClosest)
        }
        if (newClosestObject) {
          const eventOverClosest = new InteractiveEvent('mouseover')
          this.dispatch(newClosestObject, eventOverClosest)
        }
        this.closestObject = newClosestObject
      }

      let eventLeave: InteractiveEvent

      this.interactiveObjects.forEach((object) => {
        if (!object.intersected && object.wasIntersected) {
          if (!eventLeave)
            eventLeave = new InteractiveEvent('mouseleave')

          this.dispatch(object, eventLeave)
        }
      })

      let eventEnter: InteractiveEvent

      this.interactiveObjects.forEach((object) => {
        if (object.intersected && !object.wasIntersected) {
          if (!eventEnter)
            eventEnter = new InteractiveEvent('mouseenter')

          this.dispatch(object, eventEnter)
        }
      })
    }

    this.intersectionDirty = false
  }

  private throttleUpdateFn = throttle(this.baseUpdateFn, 100)

  private throttleUpdate = () => {
    return this.throttleUpdateFn()
  }

  public getActiveEventTypes = (): Set<string> => {
    return new Set(this.activeEventTypes.keys())
  }

  public hasActiveEvents = (): boolean => {
    return this.activeEventTypes.size > 0
  }

  public needsContinuousUpdates = (): boolean => {
    return this.needsContinuousUpdate
  }

  public registerEventType = (eventType: string) => {
    this.activeEventTypes.set(eventType, (this.activeEventTypes.get(eventType) || 0) + 1)
    this.updateOptimizationFlags()
  }

  public unregisterEventType = (eventType: string) => {
    const count = this.activeEventTypes.get(eventType) || 0
    if (count > 1)
      this.activeEventTypes.set(eventType, count - 1)
    else
      this.activeEventTypes.delete(eventType)

    this.updateOptimizationFlags()
  }

  /**
   * 更新循环
   * 通常在渲染循环中调用。
   * 根据优化标志决定是执行节流更新还是立即更新。
   */
  public update = () => {
    // Only run throttled update if we actually need intersection calculations
    if (this.needsContinuousUpdate)
      this.throttleUpdate()
    else if (this.needsIntersectionUpdate)
      this.baseUpdateFn()
  }
}